import type { ButtonProps } from '@coursera/cds-core';

import {
  getBannerTitle,
  getBannerTitleComponent,
  getEnrollButtonLabel,
  getEnrollButtonTrackingName,
  getIsEnrollButtonDisabled,
  getIsEnrolled,
  getPreEnrollmentFlowVariant,
  getPriceString,
  getShowOpenAidModal,
  getSubtitleElement,
} from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import { useGetCourseForEnrollButton } from 'bundles/enroll-common/hooks/useGetCourseForEnrollButton';
import { gqlCourseTypeToNaptimeCourseType } from 'bundles/enroll-common/utils/courseTypeUtils';
import { useIsLimitedFreeTrialEnabled } from 'bundles/enroll-common/utils/freeTrialOptimizationsUtils';
import { useFreeTrialRemovalTreatment } from 'bundles/enroll-common/utils/freeTrialRemovalExperimentUtils';
import { previewSurfacePreviewLink } from 'bundles/enroll-common/utils/freemiumModelUtils';
import useCheckS12nUpgradeEligibility from 'bundles/s12n-common/components/s12n-versioning-v2/useCheckS12nUpgradeEligibility';

const useEnrollButtonDerivedProps = ({
  enrollmentAvailableChoices,
  productPrice,
  s12nDerivative,
  multiS12nDerivatives,
  ownableProducts,
  isSpecialization,
  s12nId,
  s12nCourses,
  courseCount,
  onlyShowButton,
  name,
  isCourseraPlusEligibleProduct,
  financialAidApplicationsByUserAndProduct,
  financialAidApplications,
  productVariant,
  course: _course,
  router,
  isEligibleForPlusMonthlyToAnnualUpgrade,
  showUndoCancellationCTA,
  s12n,
  s12ns,
  courses,
  courseType,
  ...rest
}: {
  [x: string]: any;
}) => {
  const isEnrolled = getIsEnrolled({ enrollmentAvailableChoices, isSpecialization });
  const isCapstoneAccessLocked = enrollmentAvailableChoices?.isCapstoneAccessLocked;
  const canEnrollThroughCourseraPlus = enrollmentAvailableChoices?.canEnrollThroughCourseraPlus;
  const preEnrollmentFlowVariant = getPreEnrollmentFlowVariant({
    enrollmentAvailableChoices,
  });

  const course = useGetCourseForEnrollButton({ s12nCourses, course: _course, isSpecialization, isEnrolled });

  const courseTypeMetadataWithVersion = gqlCourseTypeToNaptimeCourseType(courseType);

  const priceString = getPriceString({
    multiS12nDerivatives,
    s12nDerivative,
    ownableProducts,
    productPrice,
  });
  const TitleComponent = getBannerTitleComponent({
    enrollmentAvailableChoices,
    onlyShowButton,
    preEnrollmentFlowVariant,
  });
  const isStandaloneCourse = !isSpecialization && (!s12ns || s12ns.length < 1);

  const surfacePreviewLinkVariant =
    course && enrollmentAvailableChoices
      ? previewSurfacePreviewLink(enrollmentAvailableChoices, course.id, courseTypeMetadataWithVersion)
      : undefined;

  const isFreeTrialRemovalTreatment = useFreeTrialRemovalTreatment(
    enrollmentAvailableChoices?.canSubscribeToS12n ?? false
  );
  const isLimitedFreeTrialEnabled = useIsLimitedFreeTrialEnabled();

  const title = getBannerTitle({
    priceString,
    enrollmentAvailableChoices,
    isSpecialization,
    onlyShowButton,
    preEnrollmentFlowVariant,
    isStandaloneCourse,
    surfacePreviewLinkVariant,
    s12nId,
    isFreeTrialRemovalTreatment,
    isLimitedFreeTrialEnabled,
  });

  const subtitleElem = getSubtitleElement({
    onlyShowButton,
    isCapstoneAccessLocked,
    courseCount,
    isEnrolled,
    canEnrollThroughCourseraPlus,
    courseName: name,
  });

  const buttonSize: ButtonProps['size'] = onlyShowButton ? 'small' : 'medium';
  const trackingName = onlyShowButton ? 'sticky_header' : 'top_banner';

  const { openFinAidInfo, showOpenAidModal } = getShowOpenAidModal({
    financialAidApplications: financialAidApplications?.elements,
    financialAidApplicationsByUserAndProduct,
    isSpecialization,
    s12nId,
    courses: s12nCourses,
  });

  const eligibilityData = useCheckS12nUpgradeEligibility({ s12nId, isSpecialization });
  const isS12nUpgradeEligible = eligibilityData?.upgradeEligibility ?? false;
  const currentS12nId = eligibilityData?.currentEnrolledS12nInFamily?.id ?? '';
  const lastActiveCourseSlug = eligibilityData?.currentEnrolledS12nInFamily?.lastActiveCourse?.slug ?? '';

  // TODO (htran) figure out why the test fails on initial run with this prop missing
  if (!course) {
    return rest;
  }

  return {
    isEnrolled,
    title,
    subtitleElem,
    buttonSize,
    courseId: course.id,
    course,
    buttonLabel: getEnrollButtonLabel({
      enrollmentAvailableChoices,
      isSpecialization,
      course,
      preEnrollmentFlowVariant,
      s12nId,
      s12n,
      s12ns,
      pathname: router.location.pathname,
      isEligibleForPlusMonthlyToAnnualUpgrade,
      showUndoCancellationCTA,
      isS12nUpgradeEligible,
      courseTypeMetadataWithVersion,
      isStandaloneCourse,
    }),
    buttonDisabled: getIsEnrollButtonDisabled({
      enrollmentAvailableChoices,
      isSpecialization,
      course,
    }),
    buttonTrackingName: getEnrollButtonTrackingName({ enrollmentAvailableChoices }),
    isSpecialization,
    // `onlyShowButton` is only true for the sticky SCDP header
    // don't want to check params for that because then we will duplicate opening the enroll modal with the main enroll section
    shouldCheckActionParams: !onlyShowButton,
    showOpenAidModal,
    openFinAidInfo,
    isEligibleForPlusMonthlyToAnnualUpgrade,
    isCourseraPlusEligibleProduct,
    trackingName,
    productVariant,
    s12nProductVariant: productVariant,
    enrollmentAvailableChoices,
    router,
    TitleComponent,
    isS12nUpgradeEligible,
    currentS12nId,
    ...rest,
    partnerIds: isSpecialization ? s12n?.partnerIds : course?.partnerIds,
    lastActiveCourseSlug,
    courseTypeMetadataWithVersion,
  };
};

export default useEnrollButtonDerivedProps;
